import AsyncStorage from '@react-native-async-storage/async-storage';
import type { LabelScaleAdapterId } from '../domain/labelScaleExport';
import {
  DEFAULT_BRANCH_SCALE_CONFIG,
  type BranchScaleConfig,
  type ScaleProtocol,
} from '../domain/scaleConfig';

// Config por dispositivo (AsyncStorage local), igual que printConfigRepo.
const STORAGE_KEY = '@vuelto/scale_config_by_device';
const LEGACY_STORAGE_KEY = '@vuelto/scale_config_by_branch';

type StoredMap = Record<string, Partial<BranchScaleConfig>>;

async function readMap(): Promise<StoredMap> {
  try {
    const raw = await AsyncStorage.getItem(STORAGE_KEY);
    const map = raw ? (JSON.parse(raw) as StoredMap) : {};
    // Migración lazy de la key legacy (pre-fix): mergea, persiste en la key
    // nueva y descarta la legacy (fire-and-forget; el merge ya quedó en RAM).
    const legacyRaw = await AsyncStorage.getItem(LEGACY_STORAGE_KEY);
    if (legacyRaw) {
      const merged = { ...(JSON.parse(legacyRaw) as StoredMap), ...map };
      // Await: un setItem fire-and-forget podría pisar un writeMap posterior.
      await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(merged)).catch(() => {});
      void AsyncStorage.removeItem(LEGACY_STORAGE_KEY).catch(() => {});
      return merged;
    }
    return map;
  } catch {
    return {};
  }
}

async function writeMap(map: StoredMap): Promise<void> {
  // Propagar el error como en printConfigRepo: un catch silencioso mostraba
  // "Balanza actualizada" aunque la config no se hubiera guardado (#1295).
  await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(map));
}

function normalizeProtocol(value: unknown): ScaleProtocol {
  if (value === 'toledo' || value === 'bizerba' || value === 'mock') return value;
  return DEFAULT_BRANCH_SCALE_CONFIG.protocol;
}

function normalizeLabelAdapter(value: unknown): LabelScaleAdapterId {
  if (value === 'kretz_report_file' || value === 'systel_csv') return value;
  return DEFAULT_BRANCH_SCALE_CONFIG.labelExportAdapter;
}

function normalizeConfig(partial: Partial<BranchScaleConfig> | undefined): BranchScaleConfig {
  const base = DEFAULT_BRANCH_SCALE_CONFIG;
  return {
    enabled: partial?.enabled === true,
    protocol: normalizeProtocol(partial?.protocol),
    comPort: typeof partial?.comPort === 'string' ? partial.comPort.trim() : base.comPort,
    agentUrl: partial?.agentUrl?.trim() || base.agentUrl,
    labelScaleSyncEnabled: partial?.labelScaleSyncEnabled === true,
    labelExportAdapter: normalizeLabelAdapter(partial?.labelExportAdapter),
  };
}

export async function getBranchScaleConfig(branchId: string): Promise<BranchScaleConfig> {
  if (!branchId.trim()) return DEFAULT_BRANCH_SCALE_CONFIG;
  const map = await readMap();
  return normalizeConfig(map[branchId]);
}

export async function setBranchScaleConfig(
  branchId: string,
  patch: Partial<BranchScaleConfig>,
): Promise<BranchScaleConfig> {
  if (!branchId.trim()) return DEFAULT_BRANCH_SCALE_CONFIG;
  const map = await readMap();
  const next = normalizeConfig({ ...map[branchId], ...patch });
  map[branchId] = next;
  await writeMap(map);
  return next;
}
